import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  deleteStock,
  fetchStockCapCategory,
  getStockCoverage,
  getStocks,
  recomputeCapCategories,
  refreshCoverageFromNse,
  refreshCapCategoriesFromNse,
  startQuoteCapRefresh,
  getCapRefreshStatus,
  updateStock,
  updateStockCoverage,
} from "./StockService";
import { getPositionSnapshot } from "./StockTradeService";

export default function StockList({ refreshKey }) {
  const [stocks, setStocks] = useState([]);
  const [positions, setPositions] = useState([]);
  const [coverage, setCoverage] = useState({});
  const [editId, setEditId] = useState(null);
  const [editSectors, setEditSectors] = useState("");
  const [editCountries, setEditCountries] = useState("");
  const [editCapCategory, setEditCapCategory] = useState("");
  const [capUpdatingId, setCapUpdatingId] = useState(null);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [capJob, setCapJob] = useState({ running: false });
  const [visibleCount, setVisibleCount] = useState(30);
  const sentinelRef = useRef(null);

  useEffect(() => {
    load();
  }, [refreshKey]);

  useEffect(() => {
    let timer = null;
    const poll = async () => {
      const status = await getCapRefreshStatus();
      setCapJob(status || { running: false });
      if (status?.running) {
        timer = setTimeout(poll, 4000);
      }
    };
    poll();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const load = async () => {
    const [data, pos] = await Promise.all([getStocks(), getPositionSnapshot()]);
    setStocks(data || []);
    setPositions(pos || []);
    setVisibleCount(30);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete stock?")) return;
    await deleteStock(id);
    load();
  };

  const startEdit = (stockId) => {
    setEditId(stockId);
    const cov = coverage[stockId] || { sectors: [], countries: [] };
    setEditSectors((cov.sectors || []).join(", "));
    setEditCountries((cov.countries || []).join(", "));
    const stock = stocks.find((s) => s.id === stockId);
    setEditCapCategory(stock?.capCategoryCode || "");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditSectors("");
    setEditCountries("");
    setEditCapCategory("");
  };

  const saveEdit = async () => {
    if (!editId) return;
    const sectors = editSectors.split(",").map((s) => s.trim()).filter(Boolean);
    const countries = editCountries.split(",").map((s) => s.trim()).filter(Boolean);
    if (editCapCategory) {
      await updateStock(editId, { capCategoryCode: editCapCategory });
    }
    await updateStockCoverage(editId, { sectors, countries });
    cancelEdit();
    load();
  };

  const updateCapFromNse = async (stockId) => {
    setCapUpdatingId(stockId);
    try {
      await fetchStockCapCategory(stockId);
      await load();
    } catch (err) {
      window.alert(err?.message || "Failed to fetch cap category");
    } finally {
      setCapUpdatingId(null);
    }
  };

  const handleRefreshAllCaps = async () => {
    setIsBulkUpdating(true);
    try {
      const result = await refreshCapCategoriesFromNse();
      await load();
      if (result) {
        window.alert(
          `Cap refresh done. Universe: ${result.universeSymbols}, Market caps: ${result.marketCapsFound}, Updated: ${result.stocksUpdated}`
        );
      }
    } catch (err) {
      window.alert(err?.message || "Failed to refresh cap categories");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleStartQuoteCaps = async () => {
    try {
      const status = await startQuoteCapRefresh();
      setCapJob(status || { running: true });
    } catch (err) {
      window.alert(err?.message || "Failed to start cap refresh job");
    }
  };

  const handleRecomputeCaps = async () => {
    setIsBulkUpdating(true);
    try {
      await recomputeCapCategories();
      await load();
    } catch (err) {
      window.alert(err?.message || "Failed to recompute cap categories");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleRefreshCoverage = async () => {
    setIsBulkUpdating(true);
    try {
      const result = await refreshCoverageFromNse();
      await load();
      if (result) {
        const parts = [`Updated: ${result.updated}`];
        if (Number.isFinite(result.updatedNse)) parts.push(`NSE: ${result.updatedNse}`);
        if (Number.isFinite(result.updatedBse)) parts.push(`BSE: ${result.updatedBse}`);
        window.alert(`Coverage refresh done. ${parts.join(" · ")}`);
      }
    } catch (err) {
      window.alert(err?.message || "Failed to refresh coverage");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const portfolioSet = useMemo(() => {
    const set = new Set();
    positions.forEach((p) => {
      if (p.stockId) set.add(p.stockId);
      if (p.symbol) set.add(p.symbol);
    });
    return set;
  }, [positions]);

  const sortedStocks = useMemo(() => {
    const list = [...stocks];
    list.sort((a, b) => {
      const aIn = portfolioSet.has(a.id) || portfolioSet.has(a.symbol);
      const bIn = portfolioSet.has(b.id) || portfolioSet.has(b.symbol);
      if (aIn !== bIn) return aIn ? -1 : 1;
      return (a.symbol || "").localeCompare(b.symbol || "");
    });
    return list;
  }, [stocks, portfolioSet]);

  const visibleStocks = useMemo(
    () => sortedStocks.slice(0, visibleCount),
    [sortedStocks, visibleCount]
  );

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            prev < sortedStocks.length ? Math.min(prev + 30, sortedStocks.length) : prev
          );
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [sortedStocks.length]);

  useEffect(() => {
    const missing = visibleStocks.filter((s) => !coverage[s.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      missing.map(async (s) => {
        try {
          const cov = await getStockCoverage(s.id);
          return [s.id, cov];
        } catch (err) {
          return [s.id, { sectors: [], countries: [] }];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const next = { ...coverage };
      results.forEach((res) => {
        if (res.status === "fulfilled" && res.value) {
          const [id, cov] = res.value;
          next[id] = cov || { sectors: [], countries: [] };
        }
      });
      setCoverage(next);
    });
    return () => {
      cancelled = true;
    };
  }, [visibleStocks, coverage]);

  return (
    <div className="card">
      <div className="stocks-toolbar">
        <div className="row-actions">
          <button className="btn btn-primary" onClick={handleRefreshAllCaps} disabled={isBulkUpdating}>
            {isBulkUpdating ? "Updating..." : "Update All Caps (NSE)"}
          </button>
          <button className="btn btn-primary" onClick={handleStartQuoteCaps} disabled={capJob.running}>
            {capJob.running ? "Updating Quotes..." : "Update Caps (Quotes)"}
          </button>
          <button className="btn btn-secondary" onClick={handleRecomputeCaps} disabled={isBulkUpdating}>
            Recompute Caps (Rank)
          </button>
          <button className="btn btn-secondary" onClick={handleRefreshCoverage} disabled={isBulkUpdating}>
            Update Coverage (NSE/BSE)
          </button>
        </div>
        <div className="stocks-muted" style={{ marginTop: 8 }}>
          Cap job: {capJob.running ? "Running" : "Idle"}
          {capJob.universeSymbols ? ` · Universe ${capJob.universeSymbols}` : ""}
          {capJob.updated ? ` · Updated ${capJob.updated}` : ""}
          {capJob.skipped ? ` · Skipped ${capJob.skipped}` : ""}
          {capJob.errors ? ` · Errors ${capJob.errors}` : ""}
        </div>
      </div>
      <div className="data-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Name</th>
              <th>Exchange</th>
              <th>Sector</th>
              <th>Coverage Sectors</th>
              <th>Coverage Countries</th>
              <th>Currency</th>
              <th>Market Cap</th>
              <th>Cap Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleStocks.map((s) => (
              <Fragment key={s.id}>
                <tr key={s.id}>
                  <td>{s.symbol}</td>
                  <td>{s.name}</td>
                  <td>{s.exchange}</td>
                  <td>{s.sector}</td>
                  <td>{(coverage[s.id]?.sectors || []).join(", ")}</td>
                  <td>{(coverage[s.id]?.countries || []).join(", ")}</td>
                  <td>{s.currency}</td>
                  <td>{s.marketCap}</td>
                  <td>{s.capCategoryCode || "UNKNOWN"}</td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => startEdit(s.id)}>
                      Edit Coverage
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => updateCapFromNse(s.id)}
                      disabled={capUpdatingId === s.id}
                    >
                      {capUpdatingId === s.id ? "Updating..." : "Update Cap (NSE)"}
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(s.id)}>
                      🗑️
                    </button>
                  </td>
                </tr>
                {editId === s.id && (
                  <tr>
                    <td colSpan="10">
                      <div className="stack-form stocks-inline-form">
                        <input
                          value={editSectors}
                          onChange={(e) => setEditSectors(e.target.value)}
                          placeholder="Sectors (comma separated)"
                        />
                        <input
                          value={editCountries}
                          onChange={(e) => setEditCountries(e.target.value)}
                          placeholder="Countries (comma separated)"
                        />
                        <select value={editCapCategory} onChange={(e) => setEditCapCategory(e.target.value)}>
                          <option value="">Cap category</option>
                          <option value="LARGE_CAP">Large Cap</option>
                          <option value="MID_CAP">Mid Cap</option>
                          <option value="SMALL_CAP">Small Cap</option>
                        </select>
                        <div className="row-actions">
                          <button className="btn btn-primary" onClick={saveEdit}>
                            Save
                          </button>
                          <button className="btn btn-secondary" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {stocks.length === 0 && (
              <tr>
                <td colSpan="10">No stocks found.</td>
              </tr>
            )}
          </tbody>
        </table>
        <div ref={sentinelRef} />
      </div>
    </div>
  );
}
